'use client';

// Trial + license surface (internal pages only):
//   • During the 14-day trial → a small "X days left · Upgrade" chip + a license
//     key field is always available, so a happy user can buy at any moment.
//   • After the trial with no key → a full-screen lock asking for a license key.
//     All data is preserved; entering a valid key unlocks instantly.
//
// State comes from /api/license/status (offline 14-day trial + Ed25519 key check).

import { useEffect, useState } from 'react';

export default function TrialGate() {
  const [st, setSt] = useState(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [showKey, setShowKey] = useState(false);

  const load = () => fetch('/api/license/status').then(r => r.json()).then(d => { if (d && d.success) setSt(d); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const activate = async () => {
    const k = key.trim();
    if (!k) { setMsg('Paste your license key first.'); return; }
    setBusy(true); setMsg('');
    try {
      const r = await fetch('/api/license/status', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'activate', key: k }) });
      const d = await r.json();
      if (d.success && d.licensed) { setMsg('✓ Activated — thank you!'); setTimeout(() => window.location.reload(), 700); }
      else setMsg(d.error || 'That key is not valid.');
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  if (!st || st.licensed) return null;

  const low = st.daysRemaining <= 3;

  // ---- Post-trial lock ----
  if (st.expired) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 2147483600, background: 'rgba(6,5,14,0.92)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 460, background: '#f7f9fc', border: '1px solid rgba(15,23,42,0.1)', borderRadius: 18, padding: 30, color: '#334155', textAlign: 'center', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7)' }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>⏳</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 8px' }}>Your 14-day free trial has ended</h2>
          <p style={{ fontSize: 14, color: '#475569', margin: '0 0 20px' }}>All your leads, sites and campaigns are safe. Enter a license key to unlock everything again — instantly and forever.</p>
          <input value={key} onChange={e => setKey(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') activate(); }} placeholder="Paste your license key (GLH-…)" style={{ width: '100%', padding: '13px 14px', borderRadius: 10, background: 'rgba(15,23,42,0.05)', border: '1px solid rgba(15,23,42,0.14)', color: '#fff', fontSize: 14, marginBottom: 12 }} />
          <button onClick={activate} disabled={busy} style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer', background: 'linear-gradient(135deg,#2563eb,#2563eb)', color: '#fff', opacity: busy ? 0.7 : 1 }}>{busy ? 'Activating…' : 'Unlock with license key'}</button>
          {msg && <div style={{ marginTop: 12, fontSize: 13, color: msg.startsWith('✓') ? '#059669' : '#dc2626' }}>{msg}</div>}
          <a href="/upgrade" style={{ display: 'inline-block', marginTop: 16, fontSize: 13, color: '#2563eb', textDecoration: 'none' }}>Need a key? See plans →</a>
        </div>
      </div>
    );
  }

  // ---- Active-trial chip (always-available upgrade entry) ----
  // Dock it just PAST the left sidebar (not over it) so it never covers the
  // Account / Settings items at the bottom of the nav. Tracks the sidebar width
  // variable so it shifts correctly when the sidebar collapses.
  return (
    <div style={{ position: 'fixed', left: 'calc(var(--sidebar-width, 280px) + 16px)', bottom: 16, zIndex: 2147483000 }}>
      {showKey ? (
        <div style={{ width: 300, background: '#f7f9fc', border: '1px solid rgba(15,23,42,0.12)', borderRadius: 14, padding: 14, color: '#334155', boxShadow: '0 18px 50px -18px rgba(0,0,0,0.7)' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8, color: '#1d4ed8' }}>Have a license key?</div>
          <input value={key} onChange={e => setKey(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') activate(); }} placeholder="GLH-…" style={{ width: '100%', padding: '10px 11px', borderRadius: 9, background: 'rgba(15,23,42,0.05)', border: '1px solid rgba(15,23,42,0.14)', color: '#fff', fontSize: 13, marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={activate} disabled={busy} style={{ flex: 1, padding: '9px', borderRadius: 9, border: 'none', fontWeight: 800, fontSize: 13, cursor: 'pointer', background: 'linear-gradient(135deg,#2563eb,#2563eb)', color: '#fff' }}>{busy ? '…' : 'Activate'}</button>
            <button onClick={() => setShowKey(false)} style={{ padding: '9px 12px', borderRadius: 9, border: '1px solid rgba(15,23,42,0.14)', background: 'transparent', color: '#475569', fontSize: 13, cursor: 'pointer' }}>Close</button>
          </div>
          {msg && <div style={{ marginTop: 8, fontSize: 12, color: msg.startsWith('✓') ? '#059669' : '#dc2626' }}>{msg}</div>}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: low ? 'linear-gradient(135deg,#b45309,#7c2d12)' : '#f7f9fc', border: '1px solid rgba(15,23,42,0.12)', borderRadius: 999, padding: '8px 12px', boxShadow: '0 12px 36px -14px rgba(0,0,0,0.7)' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{low ? '⚠️' : '⏳'} Trial · {st.daysRemaining} day{st.daysRemaining === 1 ? '' : 's'} left</span>
          <button onClick={() => setShowKey(true)} style={{ padding: '5px 11px', borderRadius: 999, border: 'none', fontWeight: 800, fontSize: 12, cursor: 'pointer', background: 'linear-gradient(135deg,#2563eb,#2563eb)', color: '#fff' }}>Upgrade</button>
        </div>
      )}
    </div>
  );
}
